/* eslint-disable react/jsx-props-no-spreading */
import { Skeleton } from 'antd';
import { useHistory } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Flex } from '@newlab-io/getprospect-react-components/lib/ui';

import useFetch from 'hooks/useFetch';
import { Radio } from 'components/common';
import { toggleModal } from 'data/actions/modals';
import { SystemModal } from 'components/commonStyles';
import { modalOptionsSelector, modalsSelector } from 'data/selectors/modals';
import { getPhoneNumbersCreditsPlan } from 'data/actions/plans';
import { phoneNumbersCreditsPlanSelector } from 'data/selectors/plans';
import { purchaseCreditsLoaderSelector, phoneNumbersCreditsPlanLoaderSelector } from 'data/selectors/loading';
import Loader from 'components/loading';
import { isUserGetProspectAdminSelector, priceIdKeySelector } from 'data/selectors/user';
import PaymentButton from 'components/paymentGate/components/PaymentButtons';
import { purchaseCredits, updateStripeInfo } from 'data/actions/subscriptions';
import {
  BillingBtnWrapper,
  BillingHeader,
  BillingSubHeader,
  BillingWrapper,
  PeriodWrapper,
  PriceLabel,
  SummaryRow,
  SummaryRowItem,
  SummaryTotal,
  SummaryWrapper,
} from 'components/paymentGate/styles';
import { toggleMessage } from 'data/actions/message';

import { ModalTitle, CloseIcon } from '../baseModal/styles';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const stripeTestPromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_TEST_KEY);

const BillingPhoneNumbersModal = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { visible } = useSelector(modalsSelector);
  const { credits } = useSelector(modalOptionsSelector);
  const priceIdKey = useSelector(priceIdKeySelector);
  const purchaseCreditsLoader = useSelector(purchaseCreditsLoaderSelector);
  const isAdmin = useSelector(isUserGetProspectAdminSelector);

  const { data: phoneNumbersCreditsPlan, loading } = useFetch({
    action: getPhoneNumbersCreditsPlan,
    loadingSelector: phoneNumbersCreditsPlanLoaderSelector,
    selector: phoneNumbersCreditsPlanSelector,
    cached: true,
  });

  const [item, setItem] = useState({
    price: 0,
    amount: 0,
  });

  const handleClose = () => dispatch(toggleModal('billing_phone_numbers', false));

  const addCard = () => {
    dispatch(
      updateStripeInfo((data) => {
        window.open(data.url, '_blank');
      }),
    );
  };

  const checkout = () => {
    dispatch(
      purchaseCredits({
        phoneNumbersCreditsProduct: {
          priceId: item[priceIdKey],
        },
        onSuccess: async (data) => {
          if (data?.clientSecret && data?.paymentMethodId) {
            const stripe = isAdmin ? await stripeTestPromise : await stripePromise;
            const result = await stripe.confirmCardPayment(data.clientSecret, {
              payment_method: data.paymentMethodId,
            });
            if (result.error) {
              dispatch(toggleMessage('error', { text: result.error.message }));
            }
          } else if (data?.url) {
            window.location.href = data.url;
          } else {
            history.push('/settings/subscriptions');
          }
          handleClose();
        },
      }),
    );
  };

  const skeletonOptions = {
    loading,
    paragraph: false,
    active: true,
  };

  useEffect(() => {
    if (phoneNumbersCreditsPlan) {
      const selectedPrice =
        phoneNumbersCreditsPlan.prices.find((price) => price.amount >= credits) || phoneNumbersCreditsPlan.prices[0];
      setItem(selectedPrice);
    }
  }, [phoneNumbersCreditsPlan, credits]);

  return (
    <SystemModal
      visible={visible}
      title={
        <ModalTitle>
          Checkout
          <CloseIcon onClick={handleClose} className="ic_close" />
        </ModalTitle>
      }
      closable={false}
      width={420}
      footer={false}
      onCancel={handleClose}
      bodyStyle={{ padding: 0 }}
    >
      <Loader loading={loading}>
        <BillingWrapper>
          <PeriodWrapper>
            <BillingHeader>Additional phone numbers</BillingHeader>
            <BillingSubHeader>Choose a package</BillingSubHeader>
            <Flex direction="column" gap={10}>
              {phoneNumbersCreditsPlan?.prices.map((price) => (
                <Flex key={price.stripeId} justify="space-between">
                  <Radio
                    size="default"
                    disabled={credits > price.amount}
                    checked={item.stripeId === price.stripeId}
                    onChange={() => setItem(price)}
                  >
                    {`${price.amount.toLocaleString().replace(/,/g, ' ')} numbers`}
                  </Radio>
                  <PriceLabel>{`$${price.price}`}</PriceLabel>
                </Flex>
              ))}
            </Flex>
            <SummaryWrapper>
              <BillingHeader>Summary</BillingHeader>
              <SummaryRow>
                <SummaryRowItem>Numbers</SummaryRowItem>
                <SummaryRowItem>{item?.amount?.toLocaleString().replace(/,/g, ' ')}</SummaryRowItem>
              </SummaryRow>
              <SummaryTotal>
                <SummaryRowItem>Total</SummaryRowItem>
                <SummaryRowItem>
                  <Skeleton {...skeletonOptions}>{`$${item?.price?.toFixed(2)} USD`}</Skeleton>
                </SummaryRowItem>
              </SummaryTotal>
            </SummaryWrapper>
          </PeriodWrapper>
        </BillingWrapper>
        <BillingBtnWrapper>
          <PaymentButton addCard={addCard} canAddCard isProcessing={purchaseCreditsLoader} checkout={checkout} />
        </BillingBtnWrapper>
      </Loader>
    </SystemModal>
  );
};

export default BillingPhoneNumbersModal;
